/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  ICON_DASHBOARD,
  ICON_DONUT_SMALL,
  ICON_FILE_UPLOAD,
  ICON_INSIGHTS,
  ICON_SUPERVISED_USER_CIRCLE,
  ICON_SWITCH_ACCESS_SHORTCUT,
  NavigationItem,
} from '@squiz/sds';
import { CUSTOMER_DATA_PLATFORM } from 'apps/console-core/src/Constants/DxpCapabilities/DxpCapabilityItems.constants';
import { TENANT_FEATURE_FLAGS } from 'apps/console-core/src/Constants/FeatureFlags.constants';
import React from 'react';
import { CustomerProfile } from '../Screens/CustomerProfile/CustomerProfile';
import { Customers } from '../Screens/Customers/Customers';
import { Dashboard } from '../Screens/Dashboard/Dashboard';
import { EventBuilder } from '../Screens/EventBuilder/EventBuilder';
import { Events } from '../Screens/Events/Events';
import { Exports } from '../Screens/Exports/Exports';
import { SegmentBuilder } from '../Screens/SegmentBuilder/SegmentBuilder';
import { SegmentInsights } from '../Screens/SegmentInsights/SegmentInsights';
import { Segmentation } from '../Screens/Segmentation/Segmentation';
import { screenPaths } from './ScreenPaths.constants';
import { CDP_STRINGS } from './Strings.constants';

/**
 * Dashboard screen.
 */
export const DASHBOARD: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  icon: ICON_DASHBOARD,
  key: `dashboard`,
  label: CDP_STRINGS.dashboard,
  path: screenPaths.dashboard,
  permission: PERMISSIONS.dxpSegmentation.SEGMENT_READ,
  screen: <Dashboard />,
};

/**
 * Segmentation screen.
 */
export const SEGMENTATION: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  icon: ICON_DONUT_SMALL,
  key: `segmentation`,
  label: CDP_STRINGS.segmentation,
  path: screenPaths.segmentation,
  permission: PERMISSIONS.dxpSegmentation.SEGMENT_READ,
  screen: <Segmentation />,
};

/**
 * Segment builder - create - screen.
 */
export const SEGMENT_BUILDER_CREATE: NavigationItem = {
  devices: [],
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  key: `segment-builder-create`,
  label: CDP_STRINGS.segmentBuilder,
  path: screenPaths.segmentBuilderCreate,
  permission: PERMISSIONS.dxpSegmentation.SEGMENT_CREATE,
  screen: <SegmentBuilder />,
};

/**
 * Segment builder - update - screen.
 */
export const SEGMENT_BUILDER_UPDATE: NavigationItem = {
  devices: [],
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  key: `segment-builder-update`,
  label: CDP_STRINGS.segmentBuilder,
  path: screenPaths.segmentBuilderUpdate,
  permission: PERMISSIONS.dxpSegmentation.SEGMENT_UPDATE,
  screen: <SegmentBuilder />,
};

/**
 * Events screen.
 */
export const EVENTS: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  icon: ICON_SWITCH_ACCESS_SHORTCUT,
  key: `events`,
  label: CDP_STRINGS.events,
  path: screenPaths.events,
  permission: PERMISSIONS.dxpEvents.EVENT_READ,
  screen: <Events />,
};

/**
 * Event builder - create data integration - screen.
 */
export const EVENT_BUILDER_CREATE_DATA_INTEGRATION: NavigationItem = {
  devices: [],
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  key: `event-builder-create-data-integration`,
  label: CDP_STRINGS.segmentBuilder,
  path: `${CUSTOMER_DATA_PLATFORM.path}/event/create-data-integration`,
  permission: PERMISSIONS.dxpEvents.EVENT_CREATE,
  screen: <EventBuilder type="data-integration" />,
};

/**
 * Event builder - create web behavior - screen.
 */
export const EVENT_BUILDER_CREATE_WEB_BEHAVIOR: NavigationItem = {
  devices: [],
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  key: `event-builder-create-data-integration`,
  label: CDP_STRINGS.segmentBuilder,
  path: screenPaths.eventBuilder,
  permission: PERMISSIONS.dxpEvents.EVENT_CREATE,
  screen: <EventBuilder type="web-behavior" />,
};

/**
 * Event builder - update - screen.
 */
export const EVENT_BUILDER_UPDATE: NavigationItem = {
  devices: [],
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  key: `event-builder-update`,
  label: CDP_STRINGS.segmentBuilder,
  path: screenPaths.eventBuilderUpdate,
  permission: PERMISSIONS.dxpEvents.EVENT_UPDATE,
  screen: <EventBuilder />,
};

/**
 * Single Customer View - Customer profile screen.
 */
export const SCV_CUSTOMER_PROFILE: NavigationItem = {
  devices: [],
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  key: `scv-customer-profile`,
  label: CDP_STRINGS.customerProfile,
  path: screenPaths.scvCustomerProfile,
  permission: PERMISSIONS.dxpSCV.CUSTOMER_READ,
  screen: <CustomerProfile />,
};

/**
 * Single Customer View - Segment insights screen.
 */
export const SCV_SEGMENT_INSIGHTS: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  icon: ICON_INSIGHTS,
  key: `scv-segment-insights`,
  label: CDP_STRINGS.segmentInsights,
  path: screenPaths.segmentInsights,
  permission: PERMISSIONS.dxpSCV.CUSTOMERS_READ,
  screen: <SegmentInsights />,
};

/**
 * Single Customer View - Exports screen.
 */
export const SCV_EXPORTS: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  icon: ICON_FILE_UPLOAD,
  key: `scv-exports`,
  label: CDP_STRINGS.exports,
  path: screenPaths.scvExports,
  permission: PERMISSIONS.dxpSCV.CUSTOMERS_EXPORT,
  screen: <Exports />,
};

/**
 * Single Customer View - Customers screen.
 */
export const SCV_CUSTOMERS: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.CDP_ACCESS,
  icon: ICON_SUPERVISED_USER_CIRCLE,
  key: `scv-customers`,
  label: CDP_STRINGS.customers,
  path: screenPaths.scvCustomers,
  permission: PERMISSIONS.dxpSCV.CUSTOMERS_READ,
  screen: <Customers />,
};
